import { AlertTriangle, CheckCircle2, ChevronDown, Loader2, Paperclip, Send, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";

import { filesService, FileType } from "@/api/files";
import { todoService, type TTodoCategoryGroup, type TTodoTask } from "@/api/todo";
import { SectionCard } from "@/components/dashboard/SectionCard";
import { SectionCardSkeleton } from "@/components/dashboard/SectionCardSkeleton";
import { Pagination } from "@/components/shared/Pagination";
import { Button } from "@/components/ui/Button";
import { Textarea } from "@/components/ui/Textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/Tooltip";
import { extractErrorMessage } from "@/utils/errorUtils";

type TReadinessCategoryFilter = {
  selectedCategoryIds: string[];
  isAllSelected: boolean;
};

type TInvestorReadinessTodosFeatureProps = {
  companyId?: string | null;
  externalRefreshCounter?: number;
  onRequestTodoRefresh?: () => void;
  categoryFilter?: TReadinessCategoryFilter;
};

type TTaskFormState = {
  text: string;
  files: File[];
  isSubmitting: boolean;
  error: string | null;
};

const createInitialTaskFormState = (): TTaskFormState => ({
  text: "",
  files: [],
  isSubmitting: false,
  error: null,
});

const TODOS_PAGE_SIZE = 8;

export function InvestorReadinessTodosFeature({
  companyId,
  externalRefreshCounter = 0,
  onRequestTodoRefresh,
  categoryFilter,
}: TInvestorReadinessTodosFeatureProps) {
  const [todoGroups, setTodoGroups] = useState<TTodoCategoryGroup[]>([]);
  const [todoLoading, setTodoLoading] = useState(false);
  const [todoError, setTodoError] = useState<string | null>(null);
  const [todoRefreshKey, setTodoRefreshKey] = useState(0);
  const [taskForms, setTaskForms] = useState<Record<string, TTaskFormState>>({});
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);
  const [submittedTaskIds, setSubmittedTaskIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!companyId) {
      setTodoGroups([]);
      setTodoError(null);
      setTodoLoading(false);
      setTaskForms({});
      return;
    }

    let isCancelled = false;

    const fetchTasks = async () => {
      setTodoLoading(true);
      setTodoError(null);

      const { data, error } = await todoService.getPendingTasks(companyId);

      if (isCancelled) return;

      if (error) {
        const message = await extractErrorMessage(error);
        setTodoError(message ?? "Unable to load to-do tasks right now.");
        setTodoGroups([]);
      } else {
        setTodoGroups(data?.data ?? []);
      }

      setTodoLoading(false);
    };

    void fetchTasks();

    return () => {
      isCancelled = true;
    };
  }, [companyId, todoRefreshKey, externalRefreshCounter]);

  const triggerTodoRefresh = useCallback(() => {
    setTodoRefreshKey((prev) => prev + 1);
    onRequestTodoRefresh?.();
  }, [onRequestTodoRefresh]);

  const filteredTodoGroups = useMemo(() => {
    if (!categoryFilter || categoryFilter.isAllSelected || categoryFilter.selectedCategoryIds.length === 0) {
      return todoGroups;
    }
    return todoGroups.filter((group) => categoryFilter.selectedCategoryIds.includes(group.sectionCategoryId));
  }, [todoGroups, categoryFilter]);

  // The API groups tasks by category (ordered by category UUID), which scrambles the global
  // priority order — re-sort the flat list so the highest-priority items always come first.
  const todoTasks = useMemo(
    () => filteredTodoGroups.flatMap((group) => group.tasks).sort((a, b) => b.priority - a.priority),
    [filteredTodoGroups]
  );

  // Paginate the (potentially long) flat list of to-dos. A readiness run can create ~45 items,
  // so we page through them instead of rendering one very long scroll.
  const totalPages = Math.max(1, Math.ceil(todoTasks.length / TODOS_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedTasks = useMemo(
    () => todoTasks.slice((currentPage - 1) * TODOS_PAGE_SIZE, currentPage * TODOS_PAGE_SIZE),
    [todoTasks, currentPage]
  );

  // Reset to the first page when the filter or the underlying task set changes, so we never
  // strand the user on a page that no longer exists.
  const filterKey = categoryFilter?.isAllSelected
    ? "all"
    : (categoryFilter?.selectedCategoryIds ?? []).join(",");
  useEffect(() => {
    setPage(1);
  }, [filterKey, todoRefreshKey, externalRefreshCounter]);

  const setTaskFormValue = useCallback((taskId: string, partial: Partial<TTaskFormState>) => {
    setTaskForms((prev) => {
      const current = prev[taskId] ?? createInitialTaskFormState();
      return { ...prev, [taskId]: { ...current, ...partial } };
    });
  }, []);

  const clearTaskForm = useCallback((taskId: string) => {
    setTaskForms((prev) => {
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
  }, []);

  const handleTextChange = useCallback(
    (taskId: string, value: string) => {
      setTaskFormValue(taskId, { text: value, error: null });
    },
    [setTaskFormValue]
  );

  const handleFilesChange = useCallback(
    (taskId: string, files: File[]) => {
      setTaskFormValue(taskId, { files, error: null });
    },
    [setTaskFormValue]
  );

  const handleSubmitTask = useCallback(
    async (task: TTodoTask) => {
      const formState = taskForms[task.id] ?? createInitialTaskFormState();
      const trimmedText = formState.text.trim();
      const hasFiles = formState.files.length > 0;

      if (!companyId) return;

      if (trimmedText.length === 0 && !hasFiles) {
        setTaskFormValue(task.id, { error: "Please enter a response or attach a file before sending." });
        return;
      }

      setTaskFormValue(task.id, { isSubmitting: true, error: null });

      const fileIds: string[] = [];
      if (hasFiles) {
        for (const file of formState.files) {
          // TODO_TASK_RESPONSE keeps the generic upload route from queueing a
          // knowledge-base sync; the submit endpoint uploads these files itself.
          const { data: uploadData, error: uploadError } = await filesService.uploadFile({
            file,
            fileType: FileType.TODO_TASK_RESPONSE,
            companyId,
          });

          if (uploadError || !uploadData?.data?.id) {
            const message = uploadError ? await extractErrorMessage(uploadError) : null;
            setTaskFormValue(task.id, {
              isSubmitting: false,
              error: message ?? "Failed to upload your file. Please try again.",
            });
            return;
          }
          fileIds.push(uploadData.data.id);
        }
      }

      const { error: submitError } = await todoService.submitTaskResponse(task.id, {
        responseText: trimmedText.length > 0 ? trimmedText : null,
        fileIds: fileIds.length > 0 ? fileIds : undefined,
      });

      if (submitError) {
        const message = await extractErrorMessage(submitError);
        setTaskFormValue(task.id, {
          isSubmitting: false,
          error: message ?? "Something went wrong on our end. Your response wasn't saved — please try again in a moment.",
        });
        return;
      }

      clearTaskForm(task.id);
      setSubmittedTaskIds((prev) => [...prev, task.id]);
      setTimeout(() => {
        setTodoGroups((prev) =>
          prev
            .map((group) => ({
              ...group,
              tasks: group.tasks.filter((groupTask) => groupTask.id !== task.id),
            }))
            .filter((group) => group.tasks.length > 0)
        );
        setSubmittedTaskIds((prev) => prev.filter((id) => id !== task.id));
        triggerTodoRefresh();
      }, 1800);
    },
    [clearTaskForm, companyId, setTaskFormValue, taskForms, triggerTodoRefresh]
  );

  if (!companyId) return null;

  const showBanner = !isBannerDismissed && !todoLoading && !todoError && todoTasks.length > 0;

  return (
    <>
      {showBanner && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-3 dark:border-amber-700 dark:bg-amber-950/40">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
              Start with your highest-priority items
            </p>
            <p className="mt-0.5 text-xs text-amber-800 dark:text-amber-300">
              Completing these helps Fundy generate a more accurate readiness score and analysis.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setIsBannerDismissed(true)}
            className="shrink-0 text-xs text-amber-700 underline-offset-2 hover:underline dark:text-amber-400"
            aria-label="Dismiss this reminder"
          >
            Skip for now
          </button>
        </div>
      )}

      {todoError ? (
        <p className="mt-2 text-xs text-destructive sm:text-sm">{todoError}</p>
      ) : todoLoading ? (
        <div className="mt-4 space-y-4">
          <SectionCardSkeleton variant="todo" />
          <SectionCardSkeleton variant="todo" />
        </div>
      ) : todoTasks.length === 0 ? (
        <div className="mt-4 rounded-lg border border-dashed border-border bg-muted p-4 text-xs text-muted-foreground sm:text-sm">
          Your to-do items will appear here once your readiness analysis is complete.
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          {paginatedTasks.map((task) => (
            <TodoTaskCard
              key={task.id}
              task={task}
              formState={taskForms[task.id] ?? createInitialTaskFormState()}
              disabled={!companyId}
              isSubmitted={submittedTaskIds.includes(task.id)}
              onTextChange={handleTextChange}
              onFilesChange={handleFilesChange}
              onSubmit={(todoTask) => void handleSubmitTask(todoTask)}
            />
          ))}
          <Pagination
            page={currentPage}
            total={todoTasks.length}
            pageSize={TODOS_PAGE_SIZE}
            hasPrev={currentPage > 1}
            hasNext={currentPage < totalPages}
            onPrev={() => setPage(Math.max(1, currentPage - 1))}
            onNext={() => setPage(Math.min(totalPages, currentPage + 1))}
            className="px-0 sm:px-0"
          />
        </div>
      )}
    </>
  );
}

type TTodoTaskCardProps = {
  task: TTodoTask;
  formState: TTaskFormState;
  disabled?: boolean;
  isSubmitted?: boolean;
  onTextChange: (taskId: string, value: string) => void;
  onFilesChange: (taskId: string, files: File[]) => void;
  onSubmit: (task: TTodoTask) => void;
};

function TodoTaskCardInputRow({
  taskId,
  formState,
  disabled,
  onTextChange,
  onFilesChange,
  onSubmit,
}: {
  taskId: string;
  formState: TTaskFormState;
  disabled: boolean;
  onTextChange: (taskId: string, value: string) => void;
  onFilesChange: (taskId: string, files: File[]) => void;
  onSubmit: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const isSubmitting = formState.isSubmitting;
  const canSend = !disabled && !isSubmitting && (formState.text.trim().length > 0 || formState.files.length > 0);

  useEffect(() => {
    if (formState.files.length === 0 && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [formState.files.length]);

  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newFiles = event.target.files ? Array.from(event.target.files) : [];
    if (newFiles.length > 0) {
      onFilesChange(taskId, [...formState.files, ...newFiles]);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFileByIndex = (indexToRemove: number) => {
    onFilesChange(
      taskId,
      formState.files.filter((_, i) => i !== indexToRemove)
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && canSend) {
      e.preventDefault();
      void onSubmit();
    }
  };

  return (
    <div className="relative mt-0 space-y-2">
      {formState.files.length > 0 && (
        <div className="relative z-10 space-y-2 rounded-lg border border-primary/20 bg-primary/10 px-3 py-2">
          {formState.files.map((file, index) => (
            <div
              key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
              className="flex items-center gap-2 rounded-md border border-primary/20 bg-card px-2 py-1.5 text-sm shadow-sm"
            >
              <span className="min-w-0 flex-1 truncate font-medium text-foreground">{file.name}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleRemoveFileByIndex(index);
                }}
                className="flex-shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-red-100 hover:text-red-600"
                aria-label="Remove file"
                disabled={isSubmitting}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-end gap-2">
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          multiple
          disabled={disabled || isSubmitting}
          onChange={handleFileInputChange}
        />
        <Textarea
          placeholder="Describe what you have in place, or your plan if not yet done..."
          value={formState.text}
          onChange={(e) => onTextChange(taskId, e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled || isSubmitting}
          className="min-h-9 min-w-0 flex-1 py-2 bg-muted/30 placeholder:italic dark:bg-muted/20"
          minHeight={36}
          maxHeight={200}
          rows={1}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-muted-foreground disabled:opacity-50"
          disabled={disabled || isSubmitting}
          aria-label="Attach file"
        >
          <Paperclip className="h-4 w-4" />
        </button>
        <Button
          type="button"
          onClick={() => onSubmit()}
          disabled={!canSend}
          className="h-8 flex-shrink-0 gap-2 rounded-lg bg-primary px-4 text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {isSubmitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <Send className="h-4 w-4" />
              <span className="text-sm">Send</span>
            </>
          )}
        </Button>
      </div>
      {formState.error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-800">{formState.error}</div>
      ) : null}
    </div>
  );
}

type TPriorityBucket = {
  label: string;
  className: string;
};

function getPriorityBucket(priority: number): TPriorityBucket {
  if (priority >= 8) {
    return {
      label: "High",
      className: "border-red-300 bg-red-50 text-red-800 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300",
    };
  }
  if (priority >= 4) {
    return {
      label: "Medium",
      className:
        "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
    };
  }
  return {
    label: "Low",
    className: "border-border bg-muted text-muted-foreground",
  };
}

function PriorityBadge({ priority }: { priority: number }) {
  const bucket = getPriorityBucket(priority);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={`inline-flex shrink-0 cursor-default items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium leading-4 ${bucket.className}`}
        >
          {bucket.label}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-56 text-xs">
        Priority {priority} of 10 - 10 is high priority, 6 is medium, 1 is low.
      </TooltipContent>
    </Tooltip>
  );
}

function parsePipedTask(content: string): { action: string; evidence: string } | null {
  const parts = content.split("|").map((s) => s.trim());
  if (parts.length >= 3) {
    return { action: parts[1], evidence: parts[parts.length - 1] };
  }
  if (parts.length === 2) {
    return { action: parts[0], evidence: parts[1] };
  }
  return null;
}

function getDisplayTitle(title: string): string {
  const firstPipe = title.indexOf("|");
  return firstPipe !== -1 ? title.substring(0, firstPipe).trim() : title;
}

function TodoTaskCard({
  task,
  formState,
  disabled = false,
  isSubmitted = false,
  onTextChange,
  onFilesChange,
  onSubmit,
}: TTodoTaskCardProps) {
  const [showGuide, setShowGuide] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const parsed = parsePipedTask(task.content);
  const displayTitle = getDisplayTitle(task.title);

  if (isSubmitted) {
    return (
      <SectionCard
        headerLeft={
          <div className="flex min-w-0 items-center gap-2.5">
            <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
            <h4 className="min-w-0 break-words text-sm font-semibold text-muted-foreground line-through">{displayTitle}</h4>
          </div>
        }
      >
        <p className="pl-[26px] text-xs text-muted-foreground">
          Response submitted — this item will be reviewed and may update your readiness score.
        </p>
      </SectionCard>
    );
  }

  const title = (
    <div className="flex min-w-0 items-center gap-2.5">
      <span className="h-2 w-2 shrink-0 rounded-full bg-amber-500" aria-hidden />
      <h4 className="min-w-0 break-words text-sm font-semibold text-foreground">{displayTitle}</h4>
      <PriorityBadge priority={task.priority} />
    </div>
  );

  // Collapsed: a scannable one-line row. Expanded: full detail + response input.
  if (!expanded) {
    return (
      <SectionCard
        headerLeft={title}
        headerRight={
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Respond
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
        }
      >
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="min-w-0 pl-[26px] text-left"
        >
          <p className="line-clamp-2 text-sm leading-relaxed text-muted-foreground">
            {parsed?.action ?? task.content}
          </p>
        </button>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      headerLeft={title}
      headerRight={
        <button
          type="button"
          onClick={() => setExpanded(false)}
          aria-label="Collapse"
          className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <ChevronDown className="h-4 w-4 rotate-180" />
        </button>
      }
      footer={
        <TodoTaskCardInputRow
          taskId={task.id}
          formState={formState}
          disabled={disabled}
          onTextChange={onTextChange}
          onFilesChange={onFilesChange}
          onSubmit={() => void onSubmit(task)}
        />
      }
    >
      <div className="min-w-0 space-y-3 pl-[26px]">
        {parsed ? (
          <>
            <div>
              <p className="mb-1 text-xs font-medium text-muted-foreground">What to do</p>
              <p className="text-sm leading-relaxed text-foreground">{parsed.action}</p>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-muted-foreground">Evidence needed</p>
              <p className="text-sm leading-relaxed text-foreground">{parsed.evidence}</p>
            </div>
          </>
        ) : (
          <p className="text-sm leading-relaxed text-foreground">{task.content}</p>
        )}

        <div className="rounded-md border border-border bg-muted/50">
          <button
            type="button"
            onClick={() => setShowGuide((v) => !v)}
            className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            <span>How to respond</span>
            <span className="ml-2 text-muted-foreground">{showGuide ? "▲" : "▼"}</span>
          </button>
          {showGuide && (
            <div className="space-y-2 border-t border-border px-3 py-3 text-xs text-muted-foreground">
              <p className="font-medium text-foreground">You have two options:</p>
              <div className="space-y-1">
                <p className="font-medium text-foreground">Already done or in progress?</p>
                <p>Describe what you currently have: the tool, process, or document you use. Share any numbers or results if you have them. You can also attach a file.</p>
              </div>
              <div className="space-y-1">
                <p className="font-medium text-foreground">Not done yet?</p>
                <p>That's fine: write a short plan: what you intend to do, who will handle it (even if that's a future hire or your co-founder), and roughly when. Investors value honest plans over silence.</p>
              </div>
              <p className="pt-1 text-muted-foreground">You don't need to be technical to answer. Focus on what you know today and what your next step is.</p>
            </div>
          )}
        </div>
      </div>
    </SectionCard>
  );
}
